<script lang="ts" setup>
import { computed, type PropType } from 'vue'
import UpgradeIntermediate from '~/pages/upgrade-intermediate.vue';

interface PumpInfoRow {
  dataHeader: string
  pumpOne: string
  pumpTwo: string
  pumpThree: string
  pumpFour: string
  totalPumpTime: string
}

interface ConcurrentPumpOverview {
  onePumpMs: number
  twoPumpMs: number
  threePumpMs: number
  fourPumpMs: number
  onePumpTime: string
  twoPumpTime: string
  threePumpTime: string
  fourPumpTime: string
}

const props = defineProps({
  pumpInformationData: {
    type: Array as PropType<PumpInfoRow[]>,
    required: true,
    default: () => []
  },
  concurrentPumpOverview: {
    type: Object as PropType<ConcurrentPumpOverview>,
    required: false,
    default: null
  }
});
const { hasIntermediateAccess } = useAuth();
const formatDuration = (totalDays: number) => {
  if (!totalDays || isNaN(totalDays)) return "00:00:00";
  let totalSeconds = Math.round(totalDays * 86400);
  const hours = Math.floor(totalSeconds / 3600);
  totalSeconds %= 3600;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

const formatValue = (header: string, value: string) => {
  if (value === "0" || value === "" || !value) return "—";

  // Check if this row represents duration (Net, Total, Idle)
  const isDuration = ["Total Time", "Net Time", "Idle Time"].includes(header);
  
  if (isDuration) {
    const totalDays = parseFloat(value);
    if (isNaN(totalDays)) return value;

    return formatDuration(totalDays);
  }

  return value; // Return Start/Stop dates as they are
};

const pumpStats = computed(() => {
  const data = props.pumpInformationData || [];
  const netRow = data.find(r => r.dataHeader === 'Net Time');
  const idleRow = data.find(r => r.dataHeader === 'Idle Time');

  const parse = (val: string | undefined) => {
    if (!val) return 0;
    const f = parseFloat(val);
    return isNaN(f) ? 0 : f;
  };

  const getStats = (key: keyof PumpInfoRow) => {
    const net = netRow ? parse(netRow[key] as string) : 0;
    const idle = idleRow ? parse(idleRow[key] as string) : 0;
    const total = net + idle;
    return {
      net,
      idle,
      netPct: total > 0 ? (net / total) * 100 : 0,
      idlePct: total > 0 ? (idle / total) * 100 : 0
    };
  };

  const pumps = [
    { label: 'Pump 1', ...getStats('pumpOne') },
    { label: 'Pump 2', ...getStats('pumpTwo') },
    { label: 'Pump 3', ...getStats('pumpThree') },
    { label: 'Pump 4', ...getStats('pumpFour') },
  ];

  const system = { label: 'System Total', ...getStats('totalPumpTime') };

  return { pumps, system };
});

const concurrentStats = computed(() => {
  const c = props.concurrentPumpOverview;
  if (!c) return null;

  const total = c.onePumpMs + c.twoPumpMs + c.threePumpMs + c.fourPumpMs;
  const twoPlusMs = c.twoPumpMs + c.threePumpMs + c.fourPumpMs;
  const threePlusMs = c.threePumpMs + c.fourPumpMs;
  const pct = (ms: number) => total > 0 ? (ms / total) * 100 : 0;

  const msToHHmmss = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  };

  return {
    onePct: pct(c.onePumpMs),
    twoPct: pct(c.twoPumpMs),
    threePct: pct(c.threePumpMs),
    fourPct: pct(c.fourPumpMs),
    billableTime: msToHHmmss(twoPlusMs),
    threePlusTime: msToHHmmss(threePlusMs),
  };
});

const headers = [
  { title: 'Metric', key: 'dataHeader', align: 'start', width: '200px' },
  { title: 'Pump 1', key: 'pumpOne', align: 'end' },
  { title: 'Pump 2', key: 'pumpTwo', align: 'end' },
  { title: 'Pump 3', key: 'pumpThree', align: 'end' },
  { title: 'Pump 4', key: 'pumpFour', align: 'end' },
  { title: 'System Total', key: 'totalPumpTime', align: 'end' },
] as const;

const getRowProps = (data: { item: PumpInfoRow }) => {
  const header = (data.item.dataHeader || '').toLowerCase();
  if (header.includes('net time')) return { class: 'bg-green-lighten-5' };
  if (header.includes('idle time')) return { class: 'bg-amber-lighten-5' };
  return {};
};
</script>

<template>
  <UpgradeIntermediate v-if="!hasIntermediateAccess" style="min-height: 400px;" />
  <v-container fluid v-else>
    <v-alert
      v-if="!pumpInformationData || pumpInformationData.length === 0"
      type="info"
      variant="tonal"
      text="No pump information available."
      class="mb-4"
    ></v-alert>

    <template v-else>
      <!-- Pump Charts -->
      <v-row class="mb-6">
        <v-col
          v-for="(pump, i) in pumpStats.pumps"
          :key="i"
          cols="12"
          sm="6"
          md="4"
          lg="2"
        >
          <v-card class="h-100" elevation="2">
            <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
              {{ pump.label }}
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pa-4">
              <div class="d-flex flex-column gap-2">
                <div class="d-flex rounded-pill overflow-hidden bg-grey-lighten-3" style="height: 12px; width: 100%">
                  <div class="bg-success transition-swing" :style="{ width: pump.netPct + '%' }" :title="`Net: ${formatDuration(pump.net)}`"></div>
                  <div class="bg-warning transition-swing" :style="{ width: pump.idlePct + '%' }" :title="`Idle: ${formatDuration(pump.idle)}`"></div>
                </div>
                <div class="d-flex justify-space-between text-caption text-medium-emphasis">
                  <div class="d-flex align-center"><v-badge dot color="success" inline class="mr-1"></v-badge>Net</div>
                  <div class="d-flex align-center"><v-badge dot color="warning" inline class="mr-1"></v-badge>Idle</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- System Total -->
        <v-col cols="12" sm="6" md="4" lg="2">
           <v-card class="h-100" elevation="2">
            <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
              {{ pumpStats.system.label }}
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pa-4">
              <div class="d-flex flex-column gap-2">
                <div class="d-flex rounded-pill overflow-hidden bg-grey-lighten-3" style="height: 12px; width: 100%">
                  <div class="bg-success transition-swing" :style="{ width: pumpStats.system.netPct + '%' }" :title="`Net: ${formatDuration(pumpStats.system.net)}`"></div>
                  <div class="bg-warning transition-swing" :style="{ width: pumpStats.system.idlePct + '%' }" :title="`Idle: ${formatDuration(pumpStats.system.idle)}`"></div>
                </div>
                <div class="d-flex justify-space-between text-caption text-medium-emphasis">
                  <div class="d-flex align-center"><v-badge dot color="success" inline class="mr-1"></v-badge>Net</div>
                  <div class="d-flex align-center"><v-badge dot color="warning" inline class="mr-1"></v-badge>Idle</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Table -->
      <v-card elevation="2">
        <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
          Pump Details
        </v-card-title>
        <v-divider></v-divider>
        <v-data-table
          :headers="headers"
          :items="pumpInformationData"
          :row-props="getRowProps"
          density="compact"
          hide-default-footer
          class="text-body-2"
          hover
        >
          <template v-slot:item.dataHeader="{ item }">
            <span class="font-weight-bold text-medium-emphasis">{{ item.dataHeader }}</span>
          </template>
          
          <template v-slot:item.pumpOne="{ item }">
            <span class="font-monospace">{{ formatValue(item.dataHeader, item.pumpOne) }}</span>
          </template>
          <template v-slot:item.pumpTwo="{ item }">
            <span class="font-monospace">{{ formatValue(item.dataHeader, item.pumpTwo) }}</span>
          </template>
          <template v-slot:item.pumpThree="{ item }">
            <span class="font-monospace">{{ formatValue(item.dataHeader, item.pumpThree) }}</span>
          </template>
          <template v-slot:item.pumpFour="{ item }">
            <span class="font-monospace">{{ formatValue(item.dataHeader, item.pumpFour) }}</span>
          </template>
          <template v-slot:item.totalPumpTime="{ item }">
            <span class="font-weight-bold text-blue-grey-darken-3 font-monospace">{{ formatValue(item.dataHeader, item.totalPumpTime) }}</span>
          </template>
        </v-data-table>
      </v-card>

      <div class="d-flex align-center mt-4 text-caption text-medium-emphasis">
        <v-icon icon="mdi-information-outline" size="small" class="mr-2"></v-icon>
        Time values (Total/Net/Idle) are displayed in fractional days.
      </div>

      <!-- Concurrent Pump Overview -->
      <v-card v-if="concurrentPumpOverview && concurrentStats" class="mt-6" elevation="2">
        <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4 d-flex align-center flex-wrap gap-2">
          Concurrent Pump Operation
          <v-chip class="ml-2" color="blue-grey" size="small" variant="tonal">
            2+ pumps: {{ concurrentStats.billableTime }}
          </v-chip>
          <v-chip class="ml-1" color="green-darken-2" size="small" variant="tonal">
            3+ pumps: {{ concurrentStats.threePlusTime }}
          </v-chip>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <div class="d-flex rounded-pill overflow-hidden mb-4 bg-grey-lighten-3" style="height: 16px; width: 100%">
            <div :style="{ width: concurrentStats.onePct + '%', backgroundColor: '#BDBDBD', transition: 'width 0.5s' }" :title="`1 pump: ${concurrentPumpOverview.onePumpTime}`"></div>
            <div :style="{ width: concurrentStats.twoPct + '%', backgroundColor: '#A5D6A7', transition: 'width 0.5s' }" :title="`2 pumps: ${concurrentPumpOverview.twoPumpTime}`"></div>
            <div :style="{ width: concurrentStats.threePct + '%', backgroundColor: '#4CAF50', transition: 'width 0.5s' }" :title="`3 pumps: ${concurrentPumpOverview.threePumpTime}`"></div>
            <div :style="{ width: concurrentStats.fourPct + '%', backgroundColor: '#2E7D32', transition: 'width 0.5s' }" :title="`4 pumps: ${concurrentPumpOverview.fourPumpTime}`"></div>
          </div>
          <v-row dense>
            <v-col cols="12" sm="6" md="3">
              <div class="d-flex align-center text-caption text-medium-emphasis mb-1">
                <span class="concurrent-dot mr-1" style="background-color: #BDBDBD;"></span>
                1 pump
              </div>
              <div class="font-monospace text-body-2 pl-4">{{ concurrentPumpOverview.onePumpTime }}</div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="d-flex align-center text-caption text-medium-emphasis mb-1">
                <span class="concurrent-dot mr-1" style="background-color: #A5D6A7;"></span>
                2 pumps simultaneously
              </div>
              <div class="font-monospace text-body-2 pl-4">{{ concurrentPumpOverview.twoPumpTime }}</div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="d-flex align-center text-caption text-medium-emphasis mb-1">
                <span class="concurrent-dot mr-1" style="background-color: #4CAF50;"></span>
                3 pumps simultaneously
              </div>
              <div class="font-monospace text-body-2 pl-4">{{ concurrentPumpOverview.threePumpTime }}</div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="d-flex align-center text-caption text-medium-emphasis mb-1">
                <span class="concurrent-dot mr-1" style="background-color: #2E7D32;"></span>
                4 pumps simultaneously
              </div>
              <div class="font-monospace text-body-2 pl-4">{{ concurrentPumpOverview.fourPumpTime }}</div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </template>
  </v-container>
</template>

<style scoped>
.gap-2 {
  gap: 0.5rem;
}
.font-monospace {
  font-family: 'Courier New', Courier, monospace !important;
}
.concurrent-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
</style>